import Handlebars from 'handlebars';

export type StudentType = 'undergraduate' | 'postgraduate' | 'international';
export type LeaseTerms = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12';

interface LeaseData {
  // Student Details
  studentName: string;
  studentId: string;
  dateOfBirth: string;
  gender: string;
  email: string;
  phone: string;
  
  // Property Details
  roomNumber: string;
  buildingName: string;
  startDate: string;
  endDate: string;
  monthlyRent: number;
  depositAmount: number;
  studentType: StudentType;
  terms: LeaseTerms;
  
  // Sponsor Details
  sponsorName: string;
  sponsorEmail: string;
  sponsorPhone: string;
  sponsorIdNumber: string;
  sponsorEmployer: string;
  sponsorOccupation: string;
  sponsorMonthlyIncome: string;
  sponsorWorkAddress: string;
  sponsorHomeAddress: string;
  willSignSuretyship: boolean;
  digitalSignature?: string;
  signatureDate?: string;
  termsAccepted?: boolean;
  landlordSignature?: string;
}

const coverPageTemplate = `
STUDENT LEASE AGREEMENT

This Lease Agreement (the "Agreement") is made and entered into on {{formatDate currentDate}}, by and between:

LESSOR:
Saratoga Pty Ltd
Registration No: K2020142989
("the Lessor")

and

LESSEE:
Full Name: {{studentName}}
Student ID/Passport: {{studentId}}
Date of Birth: {{formatDate dateOfBirth}}
Gender: {{titleCase gender}}
Email: {{email}}
Phone: {{phone}}
("the Lessee")

PROPERTY MANAGER:
TN Teisho
Email: nimrod@21onsaratoga.co.za
Phone: +27 76 432 3171

PROPERTY ADDRESS:
Saratoga, 21 Saratoga Avenue
Berea, Johannesburg, 2198
`;

const mainContentTemplate = `
1. STUDENT INFORMATION
   1.1. Personal Details:
        - Full Name: {{studentName}}
        - Student ID/Passport: {{studentId}}
        - Date of Birth: {{formatDate dateOfBirth}}
        - Gender: {{titleCase gender}}
   
   1.2. Contact Information:
        - Email: {{email}}
        - Phone: {{phone}}
   
   1.3. Student Status:
        - Type: {{titleCase studentType}} Student
        - Lease Term: {{titleCase terms}}

2. ACCOMMODATION DETAILS
   2.1. Property Information:
        - Building Name: {{buildingName}}
        - Unit Number: {{roomNumber}}
   
   2.2. Lease Period:
        - Start Date: {{formatDate startDate}}
        - End Date: {{formatDate endDate}}
        - Duration: {{leaseDuration startDate endDate}}

3. FINANCIAL TERMS
   3.1. Monthly Rental: R{{formatNumber monthlyRent}} (All Services Included)
   3.2. No Administration Fee
   3.3. No Security Deposit Required
   3.4. Payment Terms:
        - Monthly rent is due on or before the 1st day of each month
        - First month's rent is due before occupation
        - All payments must reference the student ID number: {{studentId}}
        - Acceptable payment methods: EFT, debit order
   3.5. Banking Details:
        Bank: ABSA
        Account No: 4099125522
        Branch Code: 632005
        Reference: {{studentId}}

4. SPONSOR DETAILS
   4.1. Personal Information:
        - Full Name: {{sponsorName}}
        - ID Number: {{sponsorIdNumber}}
   
   4.2. Contact Details:
        - Email: {{sponsorEmail}}
        - Phone: {{sponsorPhone}}
   
   4.3. Employment Information:
        - Employer: {{sponsorEmployer}}
        - Occupation: {{sponsorOccupation}}
        - Monthly Income: R{{sponsorMonthlyIncome}}
        - Work Address: {{sponsorWorkAddress}}
   
   4.4. Residential Address:
        {{sponsorHomeAddress}}
   
   {{#if willSignSuretyship}}
   4.5. Suretyship Agreement:
        The sponsor agrees to sign a suretyship agreement and accepts joint responsibility
        for all financial obligations under this lease agreement.
   {{/if}}

5. INCLUDED SERVICES
   5.1. Basic Services:
        - Water and electricity
        - High-speed fiber internet
        - Basic maintenance
        - 24/7 security services
        - Access control
        - Common area cleaning
   5.2. Additional Amenities:
        - Study areas
        - Laundry facilities
        - Common room
        - Bicycle storage

6. RULES AND REGULATIONS
   6.1. Shared/Common Areas:
        - Keep clean and tidy at all times
        - Respect noise levels
        - Follow posted guidelines
        - Report any damages immediately
   
   6.2. Quiet Hours:
        - Sunday to Thursday: 22:00 - 06:00
        - Friday to Saturday: 23:00 - 07:00
   
   6.3. Visitors:
        - Must sign in at security
        - No overnight guests without prior approval
        - Visitors must leave by 22:00
   
   6.4. General Rules:
        - No smoking in the building
        - No pets allowed
        - No alterations without written consent
        - No subletting

7. MAINTENANCE AND REPAIRS
   7.1. Lessee Responsibilities:
        - Keep the unit clean and hygienic
        - Report maintenance issues promptly
        - Allow access for repairs with reasonable notice
        - Proper use of appliances and fixtures
   
   7.2. Lessor Responsibilities:
        - Maintain structural elements
        - Repair essential services
        - Regular maintenance inspections
        - Emergency repairs

8. TERMINATION
   8.1. Early Termination:
        - Requires 20 business days' written notice
        - Subject to finding a replacement tenant
        - Administrative fee may apply
   
   8.2. Breach of Agreement:
        - Written warning for first offense
        - Immediate termination for serious breaches
        - Right to legal action reserved

9. GENERAL PROVISIONS
   9.1. Insurance:
        - Lessee responsible for personal belongings
        - Building insurance by Lessor
   
   9.2. Access:
        - Lessor may enter for maintenance with notice
        - Immediate access in emergencies
   
   9.3. Compliance:
        - With all applicable laws and regulations
        - Building rules and guidelines
        - Health and safety requirements`;

// Safe string helper
const safeString = (str: any): string => {
  if (str === null || str === undefined) return '';
  return String(str);
};

// Register Handlebars helpers with safe string handling
Handlebars.registerHelper('formatDate', function(date) {
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
});

Handlebars.registerHelper('formatNumber', function(number) {
  if (!number) return '0.00';
  try {
    return new Intl.NumberFormat('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  } catch (error) {
    console.error('Error formatting number:', error);
    return '0.00';
  }
});

Handlebars.registerHelper('titleCase', function(str) {
  const safeStr = safeString(str);
  if (!safeStr) return '';
  return safeStr.charAt(0).toUpperCase() + safeStr.slice(1).toLowerCase();
});

Handlebars.registerHelper('leaseDuration', function(startDate, endDate) {
  if (!startDate || !endDate) return '';
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + 
                  (end.getMonth() - start.getMonth());
    return `${months} months`;
  } catch (error) {
    console.error('Error calculating lease duration:', error);
    return '';
  }
});

Handlebars.registerHelper('formatDateTime', function(date) {
  if (!date) return '';
  try {
    return new Date(date).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '';
  }
});

export const generateLeaseAgreement = (data: LeaseData): { coverPage: string; mainContent: string } => {
  try {
    const coverTemplate = Handlebars.compile(coverPageTemplate);
    const mainTemplate = Handlebars.compile(mainContentTemplate);
    
    return {
      coverPage: coverTemplate({
        ...data,
        currentDate: new Date().toISOString(),
      }),
      mainContent: mainTemplate({
        ...data,
        currentDate: new Date().toISOString(),
      }),
    };
  } catch (error) {
    console.error('Error generating lease agreement:', error);
    return {
      coverPage: 'Error generating lease agreement',
      mainContent: 'Please try again or contact support',
    };
  }
};